import React, { useEffect, useState } from "react";
import {
  Flex,
  Card,
  Text,
  Separator,
  Skeleton,
  Button,
} from "@radix-ui/themes";
import { TenantUserTicket } from "../../entities/TenantUserTicket";
import { TicketButton } from "./TicketButton";
import { ChevronLeftIcon, ChevronRightIcon, Dot } from "lucide-react";
import { layoutSupportTicketsStyle } from "../../entities/ChatSettingsV2";
import { getTicketsToDisplay } from "./ticketUtils";

const LIST_MAX = 2;
const CAROUSEL_HEIGHT = "130px";

function TicketListLoadingState({ style }: { style: layoutSupportTicketsStyle }) {
  if (style === "list") {
    return (
      <>
        {Array.from({ length: 2 }).map((_, index) => (
          <React.Fragment key={index}>
            <Flex direction="column" gap="1" align={"start"}>
              <Skeleton height="20px" width="70%" />
              <Skeleton height="16px" width="40%" />
            </Flex>

            {index !== LIST_MAX - 1 && <Separator orientation="horizontal" size="4" my="1" />}

          </React.Fragment>
        ))}

        <Separator
          orientation="horizontal"
          size="4"
          my="1"
        />

        <Skeleton height="16px" width="20%" />
      </>
    );
  }

  return (
    <>
      <Flex direction="column" gap="1" height={CAROUSEL_HEIGHT} justify={"between"}>
        <Flex direction="column" gap="1" align={"start"} width="100%">
          <Skeleton height="20px" width="70%" />
          <Skeleton height="16px" width="40%" />
        </Flex>

        <Flex direction="column" gap="1" align={"center"} width="100%">
          <Skeleton height="20px" width="70%" />
        </Flex>
      </Flex>
    </>
  );
}

function TicketBriffEmpty({ ticketLabel }: { ticketLabel: string }) {
  return (
    <Text size={"1"} color={"gray"} weight={"medium"}>
      {`No open ${ticketLabel}`}
    </Text>
  );
}

function TicketBriffHeader({ ticketLabel }: { ticketLabel: string }) {
  return (
    <Flex py={"2"} justify={"between"} gap={"2"}>
      <Text size={"1"} color={"gray"} weight={"medium"}>
        {`Open ${ticketLabel}`}
      </Text>
    </Flex>
  );
}

function TicketBriffList({
  openTickets,
  onSelectTicket,
  onShowAllTickets,
}: {
  openTickets: TenantUserTicket[];
  onSelectTicket: (ticket: TenantUserTicket) => void;
  onShowAllTickets: () => void;
}) {
  const displayedTickets = openTickets.slice(0, LIST_MAX);

  return (
    <Flex direction="column" gap="1">
      {displayedTickets.map((ticket, index) => (
        <React.Fragment key={ticket.id}>
          <TicketButton ticket={ticket} onClick={onSelectTicket} />

          {index !== displayedTickets.length - 1 && (
            <Separator orientation="horizontal" size="4" />
          )}
        </React.Fragment>
      ))}

      {openTickets.length > LIST_MAX && (
        <>
          <Separator orientation="horizontal" size="4" />

          <Flex px={"2"} py={"1"}>
            <Button
              size="1"
              variant="ghost"
              color="gray"
              highContrast
              onClick={onShowAllTickets}
              aria-label="Show all tickets"
            >
              {`+${openTickets.length - LIST_MAX} more`}
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
}

function TicketBriffCarousel({
  openTickets,
  onSelectTicket,
}: {
  openTickets: TenantUserTicket[];
  onSelectTicket: (ticket: TenantUserTicket) => void;
}) {
  const [shownTicketIndex, setShownTicketIndex] = useState(0);

  useEffect(() => {
    if (openTickets.length === 0) {
      setShownTicketIndex(0);
    } else {
      setShownTicketIndex((prev) => Math.min(prev, openTickets.length - 1));
    }
  }, [openTickets.length]);

  return (
    <Flex
      direction="column"
      gap="3"
      style={{
        height: CAROUSEL_HEIGHT,
        justifyContent: "space-between",
      }}
    >
      {openTickets[shownTicketIndex] != null && (
        <TicketButton
          ticket={openTickets[shownTicketIndex]}
          onClick={onSelectTicket}
        />
      )}

      <Flex justify="center" className="mt-2">
        {openTickets.length > 1 && (
          <Flex gap="2" align="center">
            <Button
              size="1"
              variant="ghost"
              color="gray"
              onClick={function () {
                setShownTicketIndex(function (prev) {
                  if (openTickets.length === 0) return 0;
                  return prev === 0 ? openTickets.length - 1 : prev - 1;
                });
              }}
              style={{
                padding: "0px",
              }}
              aria-label="Previous ticket"
            >
              <ChevronLeftIcon />
            </Button>
            <Flex align="center">
              {openTickets.length > 12 ? (
                <>
                  {openTickets.slice(0, 5).map((_, idx) => (
                    <Dot
                      onClick={() => setShownTicketIndex(idx)}
                      key={idx}
                      style={{
                        width: "8px",
                        height: "8px",
                        margin: "0 2px",
                        backgroundColor:
                          idx === shownTicketIndex ? "#0000009b" : "#e5e7eb",
                        borderRadius: "9999px",
                        display: "inline-block",
                        color: idx === shownTicketIndex ? "#0000009b" : "#e5e7eb",
                        transition: "background-color 0.2s, border 0.2s",
                      }}
                    />
                  ))}
                  <span
                    style={{
                      margin: "0 4px",
                      color: "#9ca3af",
                      fontSize: "12px",
                      userSelect: "none",
                    }}
                  >
                    {`${shownTicketIndex + 1}/${openTickets.length}`}
                  </span>
                  {openTickets.slice(-5).map((_, idx) => {
                    const realIdx = openTickets.length - 5 + idx;
                    return (
                      <Dot
                        onClick={() => setShownTicketIndex(realIdx)}
                        key={realIdx}
                        style={{
                          width: "8px",
                          height: "8px",
                          margin: "0 2px",
                          backgroundColor:
                            realIdx === shownTicketIndex ? "#0000009b" : "#e5e7eb",
                          borderRadius: "9999px",
                          display: "inline-block",
                          color: realIdx === shownTicketIndex ? "#0000009b" : "#e5e7eb",
                          transition: "background-color 0.2s, border 0.2s",
                        }}
                      />
                    );
                  })}
                </>
              ) : (
                openTickets.map((_, idx) => (
                  <Dot
                    onClick={() => setShownTicketIndex(idx)}
                    key={idx}
                    style={{
                      width: "8px",
                      height: "8px",
                      margin: "0 2px",
                      backgroundColor:
                        idx === shownTicketIndex ? "#0000009b" : "#e5e7eb",
                      borderRadius: "9999px",
                      display: "inline-block",
                      color: idx === shownTicketIndex ? "#0000009b" : "#e5e7eb",
                      transition: "background-color 0.2s, border 0.2s",
                    }}
                  />
                ))
              )}
            </Flex>
            <Button
              size="1"
              variant="ghost"
              color="gray"
              style={{
                padding: "0px",
              }}
              onClick={function () {
                setShownTicketIndex(function (prev) {
                  if (openTickets.length === 0) return 0;
                  return prev === openTickets.length - 1 ? 0 : prev + 1;
                });
              }}
              aria-label="Next ticket"
            >
              <ChevronRightIcon />
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

interface TicketBriffProps {
  style?: layoutSupportTicketsStyle;
  tickets?: TenantUserTicket[];
  isLoading?: boolean;
  onShowAllTickets?: () => void;
  onSelectTicket?: (ticket: TenantUserTicket) => void;
  statusesToDisplayOnThePage?: string[];
  endUserTicketName?: string;
}

export function TicketBriff({
  style = "list",
  tickets,
  isLoading = false,
  onShowAllTickets,
  onSelectTicket,
  statusesToDisplayOnThePage,
  endUserTicketName,
}: TicketBriffProps) {
  const ticketLabel = endUserTicketName ?? "Support Cases";
  const resolvedTickets = tickets ?? [];
  const handleShowAllTickets = onShowAllTickets ?? (() => undefined);
  const handleSelectTicket = onSelectTicket ?? (() => undefined);
  const openTickets = getTicketsToDisplay(resolvedTickets, statusesToDisplayOnThePage);
  const hasTickets = openTickets.length > 0;

  return (
    <Flex direction="column">
      <TicketBriffHeader ticketLabel={ticketLabel} />

      <Card>
        {isLoading ? (
          <TicketListLoadingState style={style} />
        ) : !hasTickets ? (
          <TicketBriffEmpty ticketLabel={ticketLabel} />
        ) : style === "list" ? (
          <TicketBriffList
            openTickets={openTickets}
            onSelectTicket={handleSelectTicket}
            onShowAllTickets={handleShowAllTickets}
          />
        ) : (
          <TicketBriffCarousel openTickets={openTickets} onSelectTicket={handleSelectTicket} />
        )}
      </Card>
    </Flex>
  );
}
